<template>
  <div
    :data-url="programmeUrl"
    class="relative group h-[180px] rounded-[14px] border border-[#22ec9b] bg-[#f8fff8] p-[16px] flex flex-col hover:z-50 shadow-none hover:shadow-none hover:border-[#01d680] transition-colors duration-200 cursor-pointer"
    @click="goToProgramme"
  >
    <!-- Top: Programme Name + 3-dot Kebab Dropdown (exact match to CohortsList) -->
    <div class="flex justify-between items-start w-full">
      <a
        :href="programmeUrl"
        class="text-[14px] font-bold text-black font-['Raleway',sans-serif] min-w-0 flex-1 m-0 leading-snug line-clamp-2 no-underline hover:text-[#043f42]"
        :title="programme.name"
        @click.stop
      >
        {{ displayName }}
      </a>
      <button
        v-if="!cohort.archived_at"
        type="button"
        aria-label="Programme options"
        class="w-10 h-10 -mr-2 -mt-2 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer shrink-0 relative group/kebab ml-1 border-none bg-transparent"
        @click.prevent.stop="toggleKebab"
      >
        <div class="flex flex-col items-center justify-center gap-[2px]">
          <div class="w-[3px] h-[3px] bg-black rounded-full" />
          <div class="w-[3px] h-[3px] bg-black rounded-full" />
          <div class="w-[3px] h-[3px] bg-black rounded-full" />
        </div>

        <!-- Stateful Kebab Dropdown -->
        <div
          v-if="isOpen"
          class="absolute top-[36px] right-0 bg-white border border-black/10 rounded-[14px] shadow-2xl z-[100] flex flex-col w-[130px] py-2"
          @click.stop
        >
          <a
            :href="mappingUrl"
            class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
            @click.stop="handleAction('edit', $event); isOpen = false"
          >Edit</a>
          <a
            href="#"
            class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
            @click.stop.prevent="handleAction('clone'); isOpen = false"
          >Clone</a>
          <a
            href="#"
            class="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 no-underline text-left block"
            @click.stop.prevent="handleAction('archive'); isOpen = false"
          >Archive</a>
          <a
            href="#"
            class="px-4 py-2 text-sm text-red-600 hover:bg-red-50 no-underline text-left block"
            @click.stop.prevent="handleAction('delete'); isOpen = false"
          >Delete</a>
        </div>
      </button>
    </div>

    <!-- Middle: Data lines (exact match to Node PFvRA: Sponsor, Students) -->
    <div class="flex flex-col gap-[6px] mt-[12px] w-full flex-grow">
      <!-- Sponsor line -->
      <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#5b5c60] min-w-0">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="shrink-0"
        >
          <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
          <path d="M6 12H4a2 2 0 0 0-2 2v8h4" />
          <path d="M18 9h2a2 2 0 0 1 2 2v11h-4" />
          <path d="M10 6h4" />
          <path d="M10 10h4" />
          <path d="M10 14h4" />
          <path d="M10 18h4" />
        </svg>
        <span class="truncate min-w-0">{{ sponsorDisplay }}</span>
      </div>

      <!-- Students line -->
      <div class="flex items-center gap-[6px] text-[12px] font-['Raleway',sans-serif] text-[#292d32] min-w-0">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="shrink-0"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle
            cx="9"
            cy="7"
            r="4"
          />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span>{{ programme.students_count || 0 }} Students</span>
      </div>
    </div>

    <!-- Bottom: Status Pill (exact match to Node PFvRA & filter pills) -->
    <div class="w-full mt-auto flex items-center gap-[10px]">
      <span
        v-if="statusKey === 'completed'"
        class="bg-[#01d680] text-[#043f42] rounded-full text-[8px] font-bold px-[10px] py-[3px] inline-flex items-center justify-center shrink-0 tracking-wide uppercase"
      >COMPLETED</span>
      <span
        v-else-if="statusKey === 'in_progress'"
        class="bg-[#f97316] text-white rounded-full text-[8px] font-bold px-[10px] py-[3px] inline-flex items-center justify-center shrink-0 tracking-wide uppercase"
      >IN PROGRESS</span>
      <span
        v-else-if="statusKey === 'archived'"
        class="bg-[#9b9b9b]/40 text-black rounded-full text-[8px] font-bold px-[10px] py-[3px] inline-flex items-center justify-center shrink-0 tracking-wide uppercase"
      >ARCHIVED</span>
      <span
        v-else
        class="bg-[#9b9b9b] text-white rounded-full text-[8px] font-bold px-[10px] py-[3px] inline-flex items-center justify-center shrink-0 tracking-wide uppercase"
      >DRAFT</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProgrammeCard',
  props: {
    programme: {
      type: Object,
      required: true
    },
    cohort: {
      type: Object,
      required: true
    }
  },
  emits: ['programme-action'],
  data () {
    return {
      isOpen: false
    }
  },
  computed: {
    displayName () {
      const name = this.programme.name || `Programme #${this.programme.id}`
      if (name.length > 34) {
        return `${name.slice(0, 34)}...`
      }
      return name
    },
    programmeUrl () {
      const cohortId = this.cohort.slug || this.cohort.id
      const progId = this.programme.slug || this.programme.id
      return this.programme.url || `/cohorts/${cohortId}/programmes/${progId}`
    },
    mappingUrl () {
      const cohortId = this.cohort.slug || this.cohort.id
      const progId = this.programme.slug || this.programme.id
      return `/cohorts/${cohortId}/programmes/${progId}/mapping`
    },
    sponsorDisplay () {
      if (this.programme.sponsor_name && this.programme.sponsor_name !== 'Unassigned') {
        return this.programme.sponsor_name
      }
      if (this.programme.sponsor_email && this.programme.sponsor_email !== '—') {
        return this.programme.sponsor_email
      }
      return 'No Sponsor'
    },
    isDraft () {
      return this.statusKey === 'draft'
    },
    statusKey () {
      const status = (this.programme.derived_status || this.programme.status || 'draft').toLowerCase()
      if (status === 'draft') return 'draft'
      if (status === 'ready' || status === 'active' || status === 'in_progress') return 'in_progress'
      if (status === 'completed') return 'completed'
      if (status === 'archived') return 'archived'
      return 'draft'
    },
    statusHumanized () {
      if (this.statusKey === 'in_progress') return 'In Progress'
      if (this.statusKey === 'completed') return 'Completed'
      if (this.statusKey === 'archived') return 'Archived'
      return 'Draft'
    }
  },
  mounted () {
    this.handleOutsideClick = () => {
      this.isOpen = false
    }
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeUnmount () {
    if (this.handleOutsideClick) {
      document.removeEventListener('click', this.handleOutsideClick)
    }
  },
  methods: {
    toggleKebab () {
      this.isOpen = !this.isOpen
    },
    closeKebab () {
      this.isOpen = false
    },
    goToProgramme (e) {
      if (e.target.closest('button, .group\\/kebab, a')) return
      window.location.href = this.programmeUrl
    },
    handleAction (action, event) {
      this.$emit('programme-action', {
        action,
        programme_id: this.programme.id,
        programme: this.programme
      })
      document.dispatchEvent(new CustomEvent('programme-action', {
        detail: {
          action,
          programme_id: this.programme.id,
          programme: this.programme
        }
      }))
    }
  }
}
</script>
